/**
 * useActiveVideoJobs
 * ==================
 * Polls `GET /api/videos/jobs/active` for the current user's org and exposes
 * a count + list of in-flight Video pipeline jobs to the UI. Used by the
 * sidebar's "Courses" nav item to surface a small badge so users know how
 * many video generations are running across all their courses, even when
 * they're on a different page.
 *
 * Polling cadence: 15 s. Lower than per-block polling (3 s in
 * ConceptVideoGenerator) because this is a global summary the user only
 * glances at, not the in-flight detail view they sit on.
 *
 * Auth-safe: silently no-ops if `enabled` is false (e.g. the user isn't
 * logged in yet, or isn't instructor+). Aborts the request on unmount.
 */
import { useEffect, useRef, useState } from 'react';
import api from '../api/client';

const POLL_INTERVAL_MS = 15000;
const ENDPOINT = '/videos/jobs/active';

const isCanceled = (e) =>
  e?.name === 'CanceledError' ||
  e?.code === 'ERR_CANCELED' ||
  e?.message === 'canceled';

export function useActiveVideoJobs({ enabled = true } = {}) {
  const [count, setCount] = useState(0);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const isMountedRef = useRef(true);
  const abortRef = useRef(null);
  // Skip polling when the tab is hidden so background tabs aren't
  // contributing to production pool pressure. An immediate fetch is
  // fired when the tab becomes visible again.
  const isVisibleRef = useRef(
    typeof document !== 'undefined' ? document.visibilityState === 'visible' : true
  );

  const fetchOnce = async () => {
    if (!enabled) return;
    if (!isVisibleRef.current) return;
    if (abortRef.current) {
      try { abortRef.current.abort(); } catch (_e) { /* noop */ }
    }
    const ctrl = new AbortController();
    abortRef.current = ctrl;
    if (isMountedRef.current) setLoading(true);
    try {
      const res = await api.get(ENDPOINT, { signal: ctrl.signal });
      if (!isMountedRef.current) return;
      const data = res.data || {};
      setCount(typeof data.count === 'number' ? data.count : (data.jobs || []).length);
      setJobs(Array.isArray(data.jobs) ? data.jobs : []);
      setError(null);
    } catch (e) {
      if (isCanceled(e)) return;
      if (isMountedRef.current) setError(e?.message || 'failed');
    } finally {
      if (isMountedRef.current) setLoading(false);
    }
  };

  useEffect(() => {
    isMountedRef.current = true;
    if (!enabled) {
      setCount(0);
      setJobs([]);
      return undefined;
    }
    fetchOnce();
    const handle = setInterval(fetchOnce, POLL_INTERVAL_MS);
    const onVis = () => {
      const visible = document.visibilityState === 'visible';
      isVisibleRef.current = visible;
      if (visible) fetchOnce();
    };
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', onVis);
    }
    return () => {
      isMountedRef.current = false;
      clearInterval(handle);
      if (typeof document !== 'undefined') {
        document.removeEventListener('visibilitychange', onVis);
      }
      if (abortRef.current) {
        try { abortRef.current.abort(); } catch (_e) { /* noop */ }
      }
    };
  }, [enabled]);

  return { count, jobs, loading, error, refresh: fetchOnce };
}

export default useActiveVideoJobs;
